import { useState, useEffect } from 'react';

/**
 * Custom hook to handle PWA installation prompt
 * Detects if app is installable and provides install function
 */
const useInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [isPWA, setIsPWA] = useState(false);

  useEffect(() => {
    // Check if running as PWA
    const checkPWA = () => {
      // Method 1: Check display mode
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      
      // Method 2: iOS specific
      const isIOSStandalone = window.navigator.standalone === true;
      
      // Method 3: Check if launched from home screen (Android)
      const isAndroidPWA = document.referrer.includes('android-app://');
      
      return isStandalone || isIOSStandalone || isAndroidPWA;
    };

    // Detect platform and browser
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isiOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
    const isAndroidDevice = /android/i.test(userAgent);
    
    // Detect specific Android browsers
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    const isEdge = /Edg/.test(userAgent);
    const isFirefox = /Firefox/.test(userAgent);
    const isSamsungInternet = /SamsungBrowser/.test(userAgent);
    const isOpera = /OPR/.test(userAgent) || /Opera/.test(userAgent);
    const isUCBrowser = /UCBrowser/.test(userAgent);
    
    // Check if Android browser supports PWA installation
    const isAndroidPWASupported = isAndroidDevice && (isChrome || isEdge);
    
    setIsIOS(isiOS);
    setIsAndroid(isAndroidDevice);
    setIsPWA(checkPWA());
    setIsInstalled(checkPWA());

    // Check if user dismissed the prompt before
    const dismissedDate = localStorage.getItem('installPromptDismissed');
    if (dismissedDate) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedDate)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) {
        console.log('📱 Install prompt dismissed recently, not showing again');
        return;
      }
    }

    // Listen for beforeinstallprompt event (Android/Desktop Chrome)
    const handleBeforeInstallPrompt = (e) => {
      console.log('beforeinstallprompt event fired!', e);
      // Prevent the default browser prompt
      e.preventDefault();
      
      // Save the event for later use
      setDeferredPrompt(e);
      setIsInstallable(true);
      console.log('App is now installable');
    };

    // Listen for app installed event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      
      // Store installation flag
      localStorage.setItem('appInstalled', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Always show instructions for mobile platforms (iOS, Android)
    if (!checkPWA() && (isiOS || isAndroidDevice)) {
      console.log('📱 Setting installable for mobile:', { isiOS, isAndroidDevice });
      setIsInstallable(true);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Function to trigger install prompt
  const promptInstall = async () => {
    console.log('promptInstall called, deferredPrompt:', !!deferredPrompt);
    
    if (!deferredPrompt) {
      console.log('No deferred prompt available');
      return false;
    }

    try {
      console.log('Calling deferredPrompt.prompt()');
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for user response
      const { outcome } = await deferredPrompt.userChoice;
      console.log('User choice:', outcome);
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
      } else {
        // Store dismissal timestamp
        localStorage.setItem('installPromptDismissed', Date.now().toString());
      }
      
      // Clear the deferred prompt
      setDeferredPrompt(null);
      setIsInstallable(false);
      
      return outcome === 'accepted';
    } catch (error) {
      console.error('Error in promptInstall:', error);
      return false;
    }
  };

  // Function to dismiss prompt
  const dismissPrompt = () => {
    setIsInstallable(false);
    
    // Store dismissal timestamp
    localStorage.setItem('installPromptDismissed', Date.now().toString());
  };

  return {
    isInstallable,    // Can the app be installed?
    isInstalled,      // Is the app already installed?
    isPWA,            // Is running as PWA?
    isIOS,            // Is iOS device?
    isAndroid,        // Is Android device?
    promptInstall,    // Function to show install prompt
    dismissPrompt,    // Function to dismiss prompt
  };
};

export default useInstallPrompt;

